import { Colors } from "../../../../utils/colors";
import RectangleButton from "../../../Buttons/RectangleButton";
import PopupModalContainer, {
  PopupModalContainerWidth,
} from "../../../Containers/PopupModalContainer";
import CustomTextField from "../../../CustomTextField";
import { HorizontalDivider } from "../../../Dividers/HorizontalDivider";
import { useCallback, useMemo, useState } from "react";
import DragDropList from "../../../Lists/DragDropList";
import DragDropListIcon from "../../../Lists/DragDropListIcon";
import { DropdownMenu, DropdownMenuItem } from "../../../DropdownMenu";
import { GuestInfo } from "@markit/common.types";
import {
  getCombineColumnsSeparator,
  SeparatorType,
} from "../../../../utils/spreadsheetUtils";
import DropdownArrowIcon from "../../../DropdownsAndTabs/DropdownArrowIcon";

export type CombineColumnsModalProps = {
  setIsVisible: (isVisible: boolean) => void;
  saveOnPress: (separator: SeparatorType) => void;
  spreadsheetRows: GuestInfo[];
  selectedColumns: number[];
  setSelectedColumns: (selectedColumns: number[]) => void;
};

const CombineColumnsModal = (props: CombineColumnsModalProps) => {
  const {
    setIsVisible,
    saveOnPress,
    spreadsheetRows,
    selectedColumns,
    setSelectedColumns,
  } = props;
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [selectedSeparator, setSelectedSeparator] = useState<SeparatorType>(
    SeparatorType.SPACE
  );

  // Finds the first spreadsheet row where the selected columns have data
  const firstValidSpreadsheetRow = useMemo(
    () =>
      // First, try to find a row where all selected columns have data
      spreadsheetRows.find((row) =>
        selectedColumns.every(
          (column) =>
            row.dataColumns &&
            row.dataColumns[column] &&
            row.dataColumns[column].trim() !== ""
        )
      ) ||
      // If not found, fall back to a row where some selected columns have data
      spreadsheetRows.find((row) =>
        selectedColumns.some(
          (column) =>
            row.dataColumns &&
            row.dataColumns[column] &&
            row.dataColumns[column].trim() !== ""
        )
      ),
    [selectedColumns, spreadsheetRows]
  );

  const combinedColumnPreview = useMemo(() => {
    return selectedColumns
      .map((column) => {
        return firstValidSpreadsheetRow?.dataColumns?.[column] ?? "";
      })
      .filter((value) => value && value.trim() !== "")
      .join(getCombineColumnsSeparator(selectedSeparator));
  }, [selectedColumns, firstValidSpreadsheetRow, selectedSeparator]);

  const handleOnDrag = useCallback(
    (newColumns: { id: string; value: string }[]) => {
      setSelectedColumns(newColumns.map((column) => parseInt(column.id)));
    },
    [setSelectedColumns]
  );

  const saveColumnsOnPress = useCallback(() => {
    saveOnPress(selectedSeparator);
  }, [saveOnPress, selectedSeparator]);

  const separatorDropdownItems: DropdownMenuItem[] = useMemo(
    () => [
      {
        title: SeparatorType.SPACE,
        icon:
          selectedSeparator === SeparatorType.SPACE
            ? "ion:checkmark-circle"
            : undefined,
        key: "space",
        onPress: () => setSelectedSeparator(SeparatorType.SPACE),
      },
      {
        title: SeparatorType.COMMA,
        icon:
          selectedSeparator === SeparatorType.COMMA
            ? "ion:checkmark-circle"
            : undefined,
        key: "comma",
        onPress: () => setSelectedSeparator(SeparatorType.COMMA),
      },
      {
        title: SeparatorType.DASH,
        icon:
          selectedSeparator === SeparatorType.DASH
            ? "ion:checkmark-circle"
            : undefined,
        key: "dash",
        onPress: () => setSelectedSeparator(SeparatorType.DASH),
      },
      {
        title: SeparatorType.UNDERSCORE,
        icon:
          selectedSeparator === SeparatorType.UNDERSCORE
            ? "ion:checkmark-circle"
            : undefined,
        key: "underscore",
        onPress: () => setSelectedSeparator(SeparatorType.UNDERSCORE),
      },
      {
        title: SeparatorType.NO_SEPARATOR,
        icon:
          selectedSeparator === SeparatorType.NO_SEPARATOR
            ? "ion:checkmark-circle"
            : undefined,
        key: "no_separator",
        onPress: () => setSelectedSeparator(SeparatorType.NO_SEPARATOR),
      },
    ],
    [selectedSeparator]
  );

  return (
    <PopupModalContainer
      headerComp={<span className="sectionTitle">Combine Columns</span>}
      valueComp={
        <div className="ColumnNormal" style={{ gap: 14 }}>
          <div className="ColumnNormal" style={{ gap: 7 }}>
            <div className="ColumnNormal" style={{ gap: 4 }}>
              <span className="bodyMedium">Selected Columns</span>
              <span className="smallBodySubtext">
                One entry from the columns selected. Choose the order that you
                want to combine the columns in.
              </span>
            </div>
            {firstValidSpreadsheetRow &&
            firstValidSpreadsheetRow.dataColumns ? (
              <DragDropList
                items={selectedColumns.map((columnIndex) => ({
                  id: String(columnIndex),
                  value:
                    firstValidSpreadsheetRow.dataColumns?.[columnIndex] ?? "",
                }))}
                handleChange={handleOnDrag}
              >
                {(
                  column: { id: string; value: string },
                  _,
                  dragHandleProps: any
                ) => (
                  <div className="AlignedRow" style={{ gap: 7 }}>
                    <CustomTextField
                      value={column.value}
                      placeholder="Empty Entry"
                      inputMode="text"
                      borderRadius={12}
                      backgroundColor={Colors.WHITE}
                      onChange={() => {}}
                      altMarginBottom={0}
                      altPadding="14px 14px"
                      containerStyles={{
                        pointerEvents: "none",
                        color: Colors.GRAY1,
                      }}
                    />
                    <DragDropListIcon dragHandleProps={dragHandleProps} />
                  </div>
                )}
              </DragDropList>
            ) : null}
          </div>
          <div className="ColumnNormal" style={{ gap: 7 }}>
            <span className="bodyMedium">Separator</span>
            <DropdownMenu
              dropdownItems={separatorDropdownItems}
              trigger={["click"]}
              placement="bottom"
              onOpenChange={(isOpen: boolean) => setDropdownVisible(isOpen)}
            >
              <div
                className="AlignedRowSpaced"
                style={{
                  padding: 14,
                  borderRadius: 12,
                  border: `1px solid ${Colors.GRAY2}`,
                }}
              >
                <span className="bodyMedium">{selectedSeparator}</span>
                <DropdownArrowIcon
                  isOpen={dropdownVisible}
                  color={Colors.BLACK}
                  height={14}
                />
              </div>
            </DropdownMenu>
          </div>
          <div className="ColumnNormal" style={{ gap: 7 }}>
            <span className="bodyMedium">Preview</span>
            <CustomTextField
              value={combinedColumnPreview}
              placeholder="No Preview"
              inputMode="text"
              borderRadius={12}
              backgroundColor={Colors.WHITE}
              containerStyles={{ pointerEvents: "none", color: Colors.GRAY1 }}
              onChange={() => {}}
              altMarginBottom={0}
              altPadding="14px 14px"
            />
          </div>
        </div>
      }
      fixedBottomContent={
        <div className="ColumnNormal">
          <HorizontalDivider altMargin={14} />
          <div className="AlignedRow" style={{ gap: 10, paddingInline: 14 }}>
            <RectangleButton
              buttonLabel={"Cancel"}
              onPress={() => setIsVisible(false)}
              altTextColor={Colors.BLACK}
              altColor={Colors.WHITE1}
              containerStyles={{
                paddingBlock: 14,
                paddingInline: 12,
                border: `0.5px solid ${Colors.GRAY2}`,
              }}
            />
            <RectangleButton
              buttonLabel={"Save"}
              onPress={saveColumnsOnPress}
              altTextColor={Colors.WHITE}
              altColor={Colors.BLACK}
              containerStyles={{ paddingBlock: 14, paddingInline: 12 }}
            />
          </div>
        </div>
      }
      noExit
      closeModal={() => setIsVisible(false)}
      modalContentWidth={PopupModalContainerWidth.SMALL}
    />
  );
};

export default CombineColumnsModal;
